import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchProjects,
  createProject,
  deleteProject,
  fetchProject,
} from "@/store/projectsSlice";
import { fetchMetrics } from "@/store/metricsSlice";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchField } from "@/components/SearchField";
import { ResourceFormFields } from "@/components/ResourceFormFields";
import * as projectsApi from "@/api/projects.api";
import type { Project, ApiKeyCreated, ResourceFormValues } from "@/types";
import { EMPTY_RESOURCE_FORM } from "@/constants/forms";

export function Projects() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const getErrorMessage = useApiErrorMessage();
  const {
    items: projects,
    current,
    loading,
  } = useAppSelector((s) => s.projects);
  const { items: allMetrics } = useAppSelector((s) => s.metrics);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [tab, setTab] = useState(0);

  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyExpiry, setNewKeyExpiry] = useState("");
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);

  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([]);

  const { control, handleSubmit, reset } = useForm<ResourceFormValues>({
    defaultValues: EMPTY_RESOURCE_FORM,
  });

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchMetrics());
  }, [dispatch]);

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalApiKeys = projects.reduce(
    (sum, p) => sum + (p.apiKeys?.length || 0),
    0,
  );

  const openCreate = () => {
    reset(EMPTY_RESOURCE_FORM);
    setCreateOpen(true);
  };

  const openDetail = async (projectId: string) => {
    await dispatch(fetchProject(projectId));
    setTab(0);
    setCreatedKey(null);
    setDetailOpen(true);
  };

  useEffect(() => {
    if (current) {
      setSelectedMetricIds(current.metrics?.map((m) => m.id) || []);
    }
  }, [current]);

  const onCreateSubmit = async (data: ResourceFormValues) => {
    try {
      await dispatch(createProject(data)).unwrap();
      notify(t("projects.created"), "success");
      setCreateOpen(false);
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteProject(deleteTarget.id)).unwrap();
      notify(t("projects.deleted"), "success");
      setDetailOpen(false);
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  const onCreateApiKey = async () => {
    if (!current || !newKeyName.trim()) return;
    try {
      const key = await projectsApi.createApiKey(current.id, {
        name: newKeyName,
        expiresAt: newKeyExpiry || undefined,
      });
      setCreatedKey(key);
      setNewKeyName("");
      setNewKeyExpiry("");
      dispatch(fetchProject(current.id));
      notify(t("projects.apiKeys.created"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDeleteApiKey = async (keyId: string) => {
    if (!current) return;
    try {
      await projectsApi.deleteApiKey(current.id, keyId);
      dispatch(fetchProject(current.id));
      notify(t("projects.apiKeys.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onUpdateMetrics = async () => {
    if (!current) return;
    try {
      await projectsApi.updateProjectMetrics(current.id, selectedMetricIds);
      dispatch(fetchProject(current.id));
      notify(t("projects.metricsAssoc.updated"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notify(t("projects.apiKeys.copySuccess"), "success");
  };

  return (
    <Box>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            color="secondary"
          >
            {t("projects.create")}
          </Button>
        }
      />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: t("projects.totalProjects"), value: projects.length },
          { label: t("projects.totalApiKeys"), value: totalApiKeys },
        ].map((card, idx) => (
          <Grid size={{ xs: 6, md: 3 }} key={idx}>
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {card.label}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t("common.search")}
      />
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("common.loading")}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {search ? t("common.noResults") : t("projects.noProjects")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((project, idx) => (
                <ListItem
                  key={project.id}
                  divider={idx < filtered.length - 1}
                  sx={{ px: 3, py: 2, cursor: "pointer" }}
                  onClick={() => openDetail(project.id)}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {project.name}
                        </Typography>
                        {project.shortName && (
                          <Chip
                            label={project.shortName}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={t("projects.apiKeysCountShort", {
                            count: project.apiKeys?.length || 0,
                          })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={project.description}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(project);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onCreateSubmit)}>
          <DialogTitle>{t("projects.create")}</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: "8px !important",
            }}
          >
            <ResourceFormFields control={control} ns="projects" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {t("common.create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {current?.name}
            </Typography>
            <IconButton
              color="error"
              onClick={() => current && setDeleteTarget(current)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label={t("projects.apiKeys.title")} />
            <Tab label={t("projects.metricsAssoc.title")} />
          </Tabs>

          {tab === 0 && (
            <Box>
              {createdKey && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(createdKey.value)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {createdKey.value}
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder={t("projects.apiKeys.name")}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={onCreateApiKey}
                  disabled={!newKeyName.trim()}
                >
                  {t("projects.apiKeys.create")}
                </Button>
              </Box>

              <List>
                {current?.apiKeys?.map((key) => (
                  <ListItem key={key.id} divider>
                    <ListItemText
                      primary={key.name}
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {key.maskedValue}
                          {key.expiresAt
                            ? ` | ${t("projects.apiKeys.expiresOn", {
                                date: new Date(
                                  key.expiresAt,
                                ).toLocaleDateString(),
                              })}`
                            : ""}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteApiKey(key.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {(!current?.apiKeys || current.apiKeys.length === 0) && (
                  <Typography
                    sx={{
                      color: "text.secondary",
                      p: 2,
                    }}
                  >
                    {t("projects.apiKeys.noKeys")}
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              {allMetrics.map((metric) => (
                <FormControlLabel
                  key={metric.id}
                  control={
                    <Checkbox
                      checked={selectedMetricIds.includes(metric.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMetricIds([
                            ...selectedMetricIds,
                            metric.id,
                          ]);
                        } else {
                          setSelectedMetricIds(
                            selectedMetricIds.filter((id) => id !== metric.id),
                          );
                        }
                      }}
                    />
                  }
                  label={`${metric.name}${metric.shortName ? ` (${metric.shortName})` : ""}`}
                />
              ))}
              {allMetrics.length === 0 && (
                <Typography
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("metrics.noMetrics")}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={onUpdateMetrics}>
                  {t("projects.metricsAssoc.update")}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>
            {t("common.back")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("projects.delete")}
        message={t("projects.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
